/**
 * A − n + quantity control. Buttons are at least 32px (40px by default) so a
 * thumb on a phone hits them; the group is labelled for screen readers.
 */
import { Minus, Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { MAX_QUANTITY } from '@/lib/staffOrder'

interface QuantityStepperProps {
  value: number
  onChange: (next: number) => void
  /** e.g. "Quantity of Puffer jacket, size XL". */
  label: string
  /** False when one more would break another rule (e.g. the voucher). */
  canIncrease?: boolean
  disabled?: boolean
  compact?: boolean
}

export function QuantityStepper({
  value,
  onChange,
  label,
  canIncrease = true,
  disabled = false,
  compact = false,
}: QuantityStepperProps) {
  const button = cn(
    'flex items-center justify-center rounded-md border border-gray-300 bg-white text-heritage-blue-700',
    'hover:bg-heritage-blue-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white',
    compact ? 'h-8 w-8' : 'h-10 w-10',
  )
  return (
    <div className="inline-flex shrink-0 items-center gap-1" role="group" aria-label={label}>
      <button
        type="button"
        className={button}
        onClick={() => onChange(value - 1)}
        disabled={disabled || value <= 1}
        aria-label="One less"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="w-7 text-center text-sm font-semibold tabular-nums text-heritage-blue-700">
        {value}
      </span>
      <button
        type="button"
        className={button}
        onClick={() => onChange(value + 1)}
        disabled={disabled || value >= MAX_QUANTITY || !canIncrease}
        aria-label="One more"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  )
}
